import { Component, Input } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import {
  CardProperty,
  EntityListData,
  EntityViewItem,
  GetEntityListDataByIdQuery,
} from '@ptg-entity-management/services/models';
import {
  clearGetEntityListDataByIdFailureAction,
  getEntityListDataByIdAction,
} from '@ptg-entity-management/store/actions';
import { EntityState } from '@ptg-entity-management/store/reducers';
import { getEntityListDataByIdSelector } from '@ptg-entity-management/store/selectors';
import { FundModel } from '@ptg-fund-list/models/fund-list.model';
import { PropertyType } from '@ptg-member/constance/metadataPropertyType.const';
import { getColumConfig, getEntityPropertyName, getEntityValue } from '@ptg-member/helper';
import * as fromReducer from '@ptg-reducers';
import { BaseComponent } from '@ptg-shared/components';
import { Align, Column, ColumnType, Row } from '@ptg-shared/controls/grid';
import { FIRST_PAGE, PageEvent } from '@ptg-shared/controls/pagination';
import { LayoutActions } from '@ptg-shared/layout/actions';
import { LayoutService } from '@ptg-shared/services/layout.service';
import { deepClone, getSortType } from '@ptg-shared/utils/common.util';
import { filter, takeUntil, tap } from 'rxjs/operators';
import * as fromLayoutReducer from '@ptg-shared/layout/reducers';
import { GUID_EMPTY } from '@ptg-shared/constance/value.const';
import { DisplayedTabName, STRING_QUERY_PARAM } from '@ptg-shared/layout/constance/layout.const';

@Component({
  selector: 'ptg-view-beneficiary-card',
  templateUrl: './view-beneficiary-card.component.html',
  styleUrls: ['./view-beneficiary-card.component.scss'],
})
export class ViewBeneficiaryCardComponent extends BaseComponent {
  @Input() memberId = '';
  @Input() cardId = '';
  @Input() title = '';
  @Input() memberEntityId = '';

  isLoading: boolean = true;
  totalRecords: number = 0;
  pageSize: number = 50;
  pageNumber: number = FIRST_PAGE;
  sortInfo?: Sort;
  cards: EntityViewItem[] = [];
  viewId: string = '';
  entitiesData: (EntityListData & Row)[] = [];
  propertiesConfig!: any;
  entityComponentId = '';
  columns: Column[] = [];

  profileNavigateRecordId: string = '';
  entityReferenceLinkedId: string = '';

  constructor(
    public readonly route: ActivatedRoute,
    private router: Router,
    public readonly layoutService: LayoutService,
    private store: Store<fromReducer.State>,
    public entityStore: Store<EntityState>,
  ) {
    super();
  }

  ngOnInit(): void {
    this.selectEntityListState();
    this.getData();
    this.registerProfileNavigationSelector();
  }

  getData(): void {
    let query: GetEntityListDataByIdQuery = {
      pageIndex: this.pageNumber,
      pageSize: this.pageSize,
      memberId: this.memberId,
      cardId: this.cardId,
      isSummary: false,
      entityId: this.memberEntityId ?? '',
    };
    if (this.sortInfo?.direction) {
      const sortField = this.sortInfo.active.split('_')[0];
      query = {
        ...query,
        sortField,
        sortType: getSortType(this.sortInfo),
      };
    }

    this.entityStore.dispatch(getEntityListDataByIdAction({ query: query }));
  }

  selectEntityListState() {
    this.entityStore
      .select(getEntityListDataByIdSelector)
      .pipe(
        tap((state) => (this.isLoading = !!state?.isLoading)),
        filter((state) => !!state && !state.isLoading),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((state) => {
        if (!state) {
          return;
        }
        this.entityStore.dispatch(clearGetEntityListDataByIdFailureAction());
        this.entityComponentId = state.payload?.entityComponentId ?? '';
        this.totalRecords = state.total ?? 0;
        this.propertiesConfig = state.payload?.cardProperties ?? [];
        this.getColumnConfigs();
        const listDataMappingId = (state.payload?.cardProperties ?? []).map((element: any) => {
          return {
            id: this.getColumnName(element),
            type: element.type,
            config: element.config,
          };
        });
        this.entitiesData = (state.payload?.rowData || [])?.map((item: any) => {
          const result: any = {};
          item?.values?.forEach((element: any) => {
            let columnName = element.entityReferencePropertyId ?? '';
            columnName =
              columnName && element.entityPropertyId
                ? `${columnName}_${element.entityPropertyId}`
                : element.entityPropertyId;

            if (element.option) {
              columnName = `${columnName}_${element.option}`;
            }

            const options = listDataMappingId.find((item) => item.id === columnName);

            result[columnName] = getEntityValue(element, options);
          });
          return { ...result, id: item.id, noOfAttachments: item?.noOfAttachments, values: item.values };
        });
      });
  }

  getColumnName(column: CardProperty): string {
    let columnName = '';
    columnName = column.propertyId;
    if (column.entityReferencePropertyId) {
      columnName = columnName + '_' + column.entityReferencePropertyId;
    }

    if (column.option) {
      columnName = columnName + '_' + column.option;
    }

    return columnName;
  }

  getColumnConfigs() {
    this.columns = deepClone(this.propertiesConfig).map((column: CardProperty) => {
      const columConfig = getColumConfig(getEntityPropertyName(column.type), column.config);
      return {
        name: this.getColumnName(column),
        header: {
          title: column.propertyLabel,
        },
        truncate: true,
        sortable: false,
        type: [ColumnType.Address, ColumnType.PersonName, ColumnType.Binary].includes(columConfig.type as ColumnType)
          ? ColumnType.Text
          : columConfig.type,
        align: column.type === PropertyType.TYPE_REGISTER ? Align.Center : undefined,
        templateArgs: {
          ...columConfig.templateArgs,
          emptyValueDisplay: '-',
          emptyString: '-',
          ...columConfig.type === ColumnType.EntityReference && {
            onClick: (row: FundModel & Row) => {
              this._onClickEntityReference(row, column);
            },
          }
        },
      };
    });
  }

  onChangePage(event: PageEvent) {
    this.pageNumber = event.pageNumber;
    this.pageSize = event.pageSize;
    this.getData();
  }

  private _onClickEntityReference(row: any & Row, column: CardProperty) {
    const rowValue = row?.values?.find((item: any) => {
      return item?.entityPropertyId === column?.propertyId;
    });
    this.profileNavigateRecordId = rowValue?.recordId ?? '';
    this.entityReferenceLinkedId = rowValue?.entityReferenceLinkedId ?? '';

    if (this.entityReferenceLinkedId && this.profileNavigateRecordId) {
      this.store.dispatch(
        LayoutActions.profileNavigationItemRequest({
          memberId: this.profileNavigateRecordId,
          entityReferenceLinkedId: this.entityReferenceLinkedId,
          isHyperlink: true
        })
      );
    }
  }

  registerProfileNavigationSelector() {
    this.store
      .pipe(select(fromLayoutReducer.selectProfileNavigationState), takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        if (state?.isHyperlink && state?.menu && state?.menu.length > 0 && this.profileNavigateRecordId) {
          if (state.memberNavigationList?.id === GUID_EMPTY) {
            return;
          }
          const overviewView = state.memberNavigationList as any;
          const isOverviewDetailView = (state.memberNavigationList as any)?.isOverviewDetailView;
          const url = `${isOverviewDetailView
            ? '/member/detail-view/true'
            : '/member/summary-view/true'}/${overviewView.id}/${overviewView.overviewViewId}/${this.profileNavigateRecordId}?${STRING_QUERY_PARAM.PROFILE_NAVIGATE}&entityReferenceLinkedId=${overviewView.entityId}`;
          const entityReferenceLinkedIdParam = new URLSearchParams(document.location.search).get("entityReferenceLinkedId");


          if(url.slice(0, url.indexOf('?')) === document.location.pathname
            && this.entityReferenceLinkedId === entityReferenceLinkedIdParam) {
              this.router.navigateByUrl(url);
            }
          else{
            this.store.dispatch(LayoutActions.clearProfileNavigationItemState());
            this.store.dispatch(
              LayoutActions.selectTab({
                tab: DisplayedTabName.IndividualParticipant,
                url,
              }),
            );
          }
        }
      }
    );
  }
}
